/**
 * Self-contained fixture types + option constants for the hoisted YaaS
 * program-builder widgets (the "+New Program" doc blocks).
 *
 * The app's full `Program` shape lives in
 * `apps/yaas-client/src/pages/shared/programMocks.ts` (447 lines, wired
 * into 34 files, drags in the whole policy graph). The widgets only read a
 * handful of config fields, so the export surface keeps a MINIMAL,
 * dependency-free `ProgramConfig` here instead of hoisting that graph.
 *
 * `ProgramConfig` is a structural SUBSET of the app's `Program`: every
 * field below exists on `Program` with a compatible type, so the app can
 * keep passing its real `Program` straight into the hoisted View (the
 * re-export shim types the prop as `ProgramConfig`, and `Program` is
 * assignable to it). Nothing app-side has to change.
 */

/** Draft ⇒ editable form; active/ended ⇒ read-only summary. */
export type ProgramConfigStatus = "in_progress" | "active" | "ended";

/** The exact subset of the app `Program` the Configuration View reads. */
export interface ProgramConfig {
    status: ProgramConfigStatus;
    /** Fixed length vs open-ended continuous. Defaults to fixed. */
    programType?: "fixed" | "continuous";
    /** For fixed-length programs: `fixed` caps rewards at `budget`, `open`
     *  runs the period with no budget cap. Defaults to fixed. */
    budgetMode?: "fixed" | "open";
    /** Total rewards budget (USDv) for fixed-budget programs. */
    budget?: number;
    /** Base APY (%) distributed. A non-zero value reads as a fixed base APY;
     *  zero reads as the variable earned APY. */
    baseApy?: number;
    /** Variable (earned) budget only: the share of earnings (%) passed to the
     *  user; the remainder is kept by the client. Defaults to 80. */
    userPct?: number;
    /** Human period label (fallback when `periodDays` is absent). */
    period: string;
    /** Exact duration in days when known. */
    periodDays?: number;
    /** Settlement cycle cadence. */
    settlementCycleLength?: number;
    settlementCycleUnit?: "days" | "weeks";
}

export interface ProgramRegionOption {
    value: string;
    label: string;
}

export interface ProgramPeriodOption {
    value: string;
    label: string;
    /** Days the period maps to. */
    days: number;
}

// Verbatim from the app `programMocks` — kept in sync structurally. If the
// app list changes, update here too (they're intentionally decoupled so the
// widget bundle carries no app import).
export const REGION_OPTIONS: ProgramRegionOption[] = [
    { value: "global", label: "Global" },
    { value: "united-states", label: "United States" },
    { value: "europe", label: "Europe" },
    { value: "latam", label: "Latin America" },
    { value: "apac", label: "Asia Pacific" },
    { value: "philippines", label: "Philippines" },
    { value: "bolivia", label: "Bolivia" }
];

export const PERIOD_OPTIONS: ProgramPeriodOption[] = [
    { value: "1m", label: "1m", days: 30 },
    { value: "3m", label: "3m", days: 90 },
    { value: "6m", label: "6m", days: 180 },
    { value: "12m", label: "12m", days: 365 }
];

/** Human label for a region value (falls back to the raw value). */
export function regionLabelOf(region: string): string {
    return REGION_OPTIONS.find((r) => r.value === region)?.label ?? region;
}

// ─── Policy fixture types ─────────────────────────────────────────────────
// The minimal SUBSET of the app's policy graph the hoisted Policies section
// reads. Structurally identical to `apps/yaas-client/src/pages/shared/
// programMocks.ts` (SliderPolicy / SelectPolicy / WalletPolicy / TranchePolicy
// / SummaryPolicy / Policy / ConfigurablePolicy) so the app can keep passing
// its real `Policy[]` straight into the hoisted View — but declared here so
// the widget bundle carries no 447-line app-mock import.

/** A reward rule driven by a single numeric value (rendered as an input). */
export interface SliderPolicy {
    kind: "slider";
    id: string;
    title: string;
    /** Renders the live description from the current numeric value. */
    describe: (value: number) => string;
    min: number;
    max: number;
    step: number;
    defaultValue: number;
    /** Optional denomination shown inside the input. `"usdpt"` renders the
     *  USDv logo + label suffix (instead of the min–max range hint). */
    unit?: "usdpt";
}

/** A reward rule with a fixed set of choices, driven by a select. */
export interface SelectPolicy {
    kind: "select";
    id: string;
    title: string;
    description: string;
    options: { value: string; label: string }[];
    defaultValue: string;
}

/** A wallet-list rule — configured by pasting one or more wallet addresses,
 *  plus a mode toggle (Whitelist / Blacklist / KYC Only). The value is JSON:
 *  `{ mode, addresses }`. In `kyc-only` mode the manual address list is
 *  unused (eligibility is gated on KYC-approved status). */
export interface WalletPolicy {
    kind: "wallet";
    id: string;
    title: string;
    description: string;
    /** Input placeholder, e.g. "Paste a wallet address". */
    placeholder: string;
    /** Mode toggle options. Each carries its own description (shown when that
     *  mode is active). First entry is the default mode. */
    modes?: { value: string; label: string; description: string }[];
}

/** A tranche rule — the user builds N tranches, each a threshold amount (USDv
 *  balance or days held) paired with a reward multiplier. The value is JSON:
 *  an array of `{ amount, multiplier }`. */
export interface TranchePolicy {
    kind: "tranche";
    id: string;
    title: string;
    description: string;
    /** Unit of each tranche's threshold amount. */
    amountUnit: "usdpt" | "days";
    /** Seeded tranches (shown on live programs / as a starting point). */
    defaultTranches?: { amount: number; multiplier: number }[];
}

/** A purpose-built signup-bonus rule — a one-time USDv bonus paid to customers
 *  who joined within a recency window. Configured by two number fields:
 *  `bonusAmount` (USDv, the one-time payout per user) and `joinedWithinDays`
 *  (the eligibility window in days). The value is JSON:
 *  `{ bonusAmount, joinedWithinDays }`.
 *
 *  Consume-once semantics — once a user receives the bonus they are permanently
 *  ineligible — are a BACKEND-enforced (stateful, per-user) trait the policy
 *  implies, NOT a UI control (see `neobankFixtures` GAPS). */
export interface NewUserPolicy {
    kind: "new-user";
    id: string;
    title: string;
    description: string;
    /** Default bonus payout (USDv) shown for a freshly-chosen policy. */
    defaultBonusAmount: number;
    /** Default eligibility window (days) shown for a freshly-chosen policy. */
    defaultJoinedWithinDays: number;
}

/** A read-only policy — its config is summarized into a single description
 *  line (no inline control). */
export interface SummaryPolicy {
    kind: "summary";
    id: string;
    title: string;
    description: string;
}

export type Policy = SliderPolicy | SelectPolicy | WalletPolicy | TranchePolicy | NewUserPolicy | SummaryPolicy;

/** Policy templates that can be created & configured inline (everything
 *  except read-only summaries). */
export type ConfigurablePolicy = SliderPolicy | SelectPolicy | WalletPolicy | TranchePolicy | NewUserPolicy;
